import { useCallback, useEffect, useRef, useState } from "react";
import {
  useGetProductListQuery,
  useLazyGetProductListQuery,
  useGetSearchProductListQuery,
  useLazyGetSearchProductListQuery,
} from "../api/product/product.api";
import type { Product } from "../api/product/product.type";
import type { TypeQuery } from "../api/user/user.type";

export type InfiniteProductListQuery = Omit<TypeQuery, "page"> & {
  limit: number;
};

function useInfinitePaginatedProducts(
  queryArgs: InfiniteProductListQuery,
  resetKey: string,
  mode: "list" | "search",
) {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [appliedResetKey, setAppliedResetKey] = useState(resetKey);
  const nextPageRef = useRef(2);
  const loadingMoreRef = useRef(false);
  const allProductsRef = useRef<Product[]>([]);

  allProductsRef.current = allProducts;

  // Clear list immediately on category/search change (avoid stale products + missing skeleton)
  const isResetPending = resetKey !== appliedResetKey;
  if (isResetPending) {
    setAppliedResetKey(resetKey);
    setAllProducts([]);
    setTotalCount(0);
    nextPageRef.current = 2;
    loadingMoreRef.current = false;
  }

  const pageOneQuery = { ...queryArgs, page: 1 };
  const skipSearch =
    mode === "search" && !(queryArgs.q && String(queryArgs.q).trim());

  const listFirst = useGetProductListQuery(pageOneQuery, {
    refetchOnMountOrArgChange: true,
    skip: mode !== "list",
  });
  const searchFirst = useGetSearchProductListQuery(pageOneQuery, {
    refetchOnMountOrArgChange: true,
    skip: mode !== "search" || skipSearch,
  });

  const [fetchListNext, listLazy] = useLazyGetProductListQuery();
  const [fetchSearchNext, searchLazy] = useLazyGetSearchProductListQuery();

  const firstPageData =
    mode === "search" ? searchFirst.data : listFirst.data;
  const isLoading =
    mode === "search" ? searchFirst.isLoading : listFirst.isLoading;
  const isFetchingFirst =
    mode === "search" ? searchFirst.isFetching : listFirst.isFetching;
  const isFetchingNext =
    mode === "search" ? searchLazy.isFetching : listLazy.isFetching;
  const fetchNextPage = mode === "search" ? fetchSearchNext : fetchListNext;

  useEffect(() => {
    if (!firstPageData) return;

    const results = firstPageData?.results ?? [];
    nextPageRef.current = 2;
    loadingMoreRef.current = false;

    if (typeof firstPageData?.count === "number") {
      setTotalCount(firstPageData.count);
    } else {
      setTotalCount(results.length);
    }

    setAllProducts(results);
  }, [firstPageData]);

  const hasMore = totalCount > allProducts.length;

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || isFetchingNext || !hasMore) return;

    const pageToLoad = nextPageRef.current;
    loadingMoreRef.current = true;

    try {
      const result = await fetchNextPage({
        ...queryArgs,
        page: pageToLoad,
      }).unwrap();

      const results = result?.results ?? [];
      if (typeof result?.count === "number") {
        setTotalCount(result.count);
      }

      if (!results.length) {
        setTotalCount(allProductsRef.current.length);
        return;
      }

      setAllProducts((prev) => {
        const existingIds = new Set(prev.map((p) => p?.id));
        const newItems = results.filter(
          (p) => p?.id != null && !existingIds.has(p.id),
        );
        return newItems.length ? [...prev, ...newItems] : prev;
      });
      nextPageRef.current = pageToLoad + 1;
    } catch {
      // ignore
    } finally {
      loadingMoreRef.current = false;
    }
  }, [fetchNextPage, queryArgs, hasMore, isFetchingNext]);

  const syncedProducts = isResetPending ? [] : allProducts;
  const displayProducts =
    syncedProducts.length > 0
      ? syncedProducts
      : (firstPageData?.results ?? []);

  return {
    allProducts: syncedProducts,
    displayProducts,
    totalCount: isResetPending ? 0 : totalCount,
    hasMore: isResetPending ? false : totalCount > allProducts.length,
    isLoading,
    isFetching: isFetchingFirst || isFetchingNext,
    isFetchingMore: isFetchingNext,
    loadMore,
  };
}

export function useInfiniteProductList(
  queryArgs: InfiniteProductListQuery,
  resetKey: string,
) {
  return useInfinitePaginatedProducts(queryArgs, resetKey, "list");
}

export function useInfiniteSearchProductList(
  queryArgs: InfiniteProductListQuery,
  resetKey: string,
) {
  return useInfinitePaginatedProducts(queryArgs, resetKey, "search");
}

export function useInfiniteScrollSentinel(
  loadMore: () => void | Promise<void>,
  hasMore: boolean,
  itemCount: number,
) {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!hasMore || !sentinel) return;

    const tryLoad = () => {
      const el = sentinelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.top <= window.innerHeight + 200) {
        void loadMore();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) tryLoad();
      },
      { rootMargin: "200px", threshold: 0.01 },
    );
    observer.observe(sentinel);
    window.addEventListener("scroll", tryLoad, { passive: true });
    window.addEventListener("resize", tryLoad, { passive: true });
    tryLoad();

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", tryLoad);
      window.removeEventListener("resize", tryLoad);
    };
  }, [loadMore, hasMore, itemCount]);

  return sentinelRef;
}
